import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Tenant } from '../../../types/tenants';
import { SubscriptionTariff } from '../../../types/subscriptions';
import { useSubscriptionTariffsRequests } from '../../../api/subscription_tariffs';
import { useChangeTenantSubscription } from '../../../api/subscriptions';
import { ERROR_SNACKBAR, SUCCESS_SNACKBAR } from '../../../consts';
import styles from './changeSubscription.module.css';

interface ChangeSubscriptionProps {
  tenant?: Tenant;
  onSuccess?: () => void;
}

export const ChangeSubscription: React.FC<ChangeSubscriptionProps> = ({ tenant, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading: tariffsLoading, error: tariffsError, query } = useSubscriptionTariffsRequests();
  const { loading: changeLoading, error: changeError, change } = useChangeTenantSubscription();
  const [tariffs, setTariffs] = useState<SubscriptionTariff[]>([]);
  const [selectedTariffId, setSelectedTariffId] = useState<string>('');

  useEffect(() => {
    const loadTariffs = async () => {
      const response = await query();
      if (response?.results) {
        setTariffs(response.results);
      }
    };

    loadTariffs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentTariffId = tenant?.subscription?.tariff?.id ?? '';

  useEffect(() => {
    setSelectedTariffId(currentTariffId);
  }, [currentTariffId]);

  const handleTariffChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTariffId(event.target.value);
  };

  const handleChangeSubscription = async () => {
    if (!selectedTariffId || changeLoading) {
      return;
    }

    const { success } = await change({ tariff: selectedTariffId });

    if (success) {
      window.dispatchEvent(new Event('tenantUpdated'));
      enqueueSnackbar(t('Change subscription successfully'), SUCCESS_SNACKBAR);
      onSuccess?.();
    } else {
      enqueueSnackbar(t('Change subscription failed'), ERROR_SNACKBAR);
    }
  };

  if (!tenant || tariffsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography component="p" variant="subtitle1" className={styles.radioLabel} color="text.primary">
        {t('Available subscription plans')}
      </Typography>
      <RadioGroup
        aria-label={t('Subscription plans')}
        name="subscription-tariff"
        value={selectedTariffId}
        onChange={handleTariffChange}
        className={styles.radioGroup}
      >
        {tariffs.map((tariff) => (
          <FormControlLabel
            key={tariff.id}
            value={tariff.id}
            control={<Radio size="small" />}
            disabled={tariff.id === currentTariffId}
            label={
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body1">{tariff.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('Price')}: {tariff.total}
                </Typography>
                {tariff.client_limit !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('Client limit')}: {tariff.client_limit}
                  </Typography>
                )}
                {tariff.equipment_limit !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('Equipment limit')}: {tariff.equipment_limit}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </RadioGroup>
      {tariffs.length === 0 && !tariffsLoading && (
        <Typography variant="body2" color="text.secondary">
          {t('No subscription plans available')}
        </Typography>
      )}
      {tariffsError?.detail && <div className={styles.errorMessage}>{tariffsError.detail}</div>}
      {changeError?.tariff?.[0]?.message && <div className={styles.errorMessage}>{changeError.tariff[0].message}</div>}

      <Button
        variant="contained"
        color="success"
        onClick={handleChangeSubscription}
        disabled={!selectedTariffId || changeLoading}
      >
        {changeLoading ? t('Saving...') : t('Update subscription')}
      </Button>
    </Box>
  );
};
